// buildResumeDoc.js
import { Document, Paragraph, TextRun } from "docx";
import hiringAuthorities from "./hiringAuthorities.js";
import displayValues from "../json/displayValues.json";
import { normalizeResumeBulletLines } from "./resumeBulletUtils";
import { isFederalResume } from "./resumeFormat";
import { formatResumeDateRange } from "./resumeDate";

/* ----------------------------- Helpers ----------------------------- */

const stripHtml = (text) => (text ? String(text).replace(/<[^>]+>/g, "") : "");

// -------------------------------------------------------------------
// Font configuration and helpers
// -------------------------------------------------------------------
// Choose a modern, ATS-friendly font for the entire document. Calibri is widely supported
// and more contemporary than Times New Roman.
const DEFAULT_FONT = "Arial";
// Heading text uses a slightly larger size (measured in half-points).
const HEADER_SIZE = 28; // 14 pt
// Normal body text size (12 pt).
const NORMAL_SIZE = 24;

/**
 * Build a heading paragraph. Section titles use bold styling and our custom font.
 * @param {string} text The heading content.
 * @returns {Paragraph}
 */
const makeHeading = (text) => {
  return new Paragraph({
    children: [
      new TextRun({
        text,
        bold: true,
        font: DEFAULT_FONT,
        size: HEADER_SIZE,
      }),
    ],
  });
};

/**
 * Build a standard paragraph with optional bold or italics. All runs use our font.
 * @param {string} text The paragraph content.
 * @param {object} options Optional styling overrides.
 * @param {boolean} options.bold When true the text is bold.
 * @param {boolean} options.italics When true the text is italicized.
 * @param {number} options.size Override the default font size (half-points).
 * @returns {Paragraph}
 */
const makeTextParagraph = (text, options = {}) => {
  const { bold = false, italics = false, size = NORMAL_SIZE } = options;
  return new Paragraph({
    children: [
      new TextRun({
        text,
        bold,
        italics,
        font: DEFAULT_FONT,
        size,
      }),
    ],
  });
};

// Bullet list from array of strings (filters out empties)
const makeBulletList = (items) => {
  if (!Array.isArray(items) || !items.length) return [];
  const clean = normalizeResumeBulletLines(items);
  return clean.map((item) => {
    return new Paragraph({
      children: [
        new TextRun({
          text: item,
          font: DEFAULT_FONT,
          size: NORMAL_SIZE,
        }),
      ],
      bullet: { level: 0 },
    });
  });
};

// Inline pipe-separated paragraph from array
const pipeInline = (arr) => {
  if (!Array.isArray(arr) || !arr.length) return null;
  const clean = arr
    .map((s) => stripHtml(typeof s === "string" ? s : String(s)))
    .filter((s) => s.trim().length > 0);
  if (!clean.length) return null;
  // Use our helper to ensure the correct font is applied
  return makeTextParagraph(clean.join(" | "));
};

// Convert a volunteer entry (string or object) into a single descriptive string.
// Objects are expected to have fields: organization, role, startDate, endDate,
// present (boolean), hoursPerWeek, responsibilities. Strings are returned as-is.
const formatVolunteer = (entry, federal = true) => {
  if (!entry) return "";
  // If the entry is a plain string, strip HTML and return
  if (typeof entry === "string" || typeof entry === "number") {
    return stripHtml(String(entry));
  }
  // Otherwise, attempt to extract known fields
  try {
    const organization = stripHtml(entry.organization || "");
    const role = stripHtml(entry.role || "");
    const startDate = stripHtml(entry.startDate || "");
    const endDate = stripHtml(entry.endDate || "");
    const present = !!entry.present;
    const hours = stripHtml(entry.hoursPerWeek || "");
    const responsibilities = stripHtml(entry.responsibilities || "");
    let parts = [];
    // Title and organization
    let titlePart = "";
    if (role) titlePart = role;
    if (organization) titlePart = titlePart ? `${titlePart} — ${organization}` : organization;
    if (titlePart) parts.push(titlePart);
    // Date range
    const datePart = formatResumeDateRange(startDate, endDate, present);
    if (datePart) parts.push(datePart);
    // Hours
    if (federal && hours) parts.push(`Hours/Week: ${hours}`);
    // Responsibilities
    if (responsibilities) parts.push(responsibilities);
    return parts.filter((s) => s && s.trim().length > 0).join(" | ");
  } catch {
    return stripHtml(String(entry));
  }
};

// Prefer AI-enhanced arrays → bullet arrays → legacy strings
export const collectWorkBullets = (job) => {
  const enhanced = [
    ...(Array.isArray(job?.enhancedResponsibilities) ? job.enhancedResponsibilities : []),
    ...(Array.isArray(job?.enhancedAccomplishments) ? job.enhancedAccomplishments : []),
  ];
  const normalizedEnhanced = normalizeResumeBulletLines(enhanced);
  if (normalizedEnhanced.length > 0) return normalizedEnhanced;

  const bullets = [
    ...(Array.isArray(job?.responsibilitiesBullets) ? job.responsibilitiesBullets : []),
    ...(Array.isArray(job?.accomplishmentsBullets) ? job.accomplishmentsBullets : []),
  ];
  const normalizedBullets = normalizeResumeBulletLines(bullets);
  if (normalizedBullets.length > 0) return normalizedBullets;

  const legacy = [];
  if (job?.responsibilities) legacy.push(job.responsibilities);
  if (job?.keyAccomplishments) legacy.push(job.keyAccomplishments);
  return normalizeResumeBulletLines(legacy);
};

export const isWorkEntryVisible = (job) => job?.showOnResume !== false;
export const isEducationEntryVisible = (entry) => entry?.showOnResume !== false;

/* --------------------- Section Order + Defaults -------------------- */

const DEFAULT_ORDER = [
  "personalInfo",
  "professionalSummary",
  "skills",
  "workHistory",
  "educationHistory",
  "certifications",
  "training",
  "volunteerWork",
  "languages",
  // Insert interests/hobbies and awards/honors after languages
  "interests",
  "awards",
  "organizations",
  "references",
  "additionalInformation",
  "militaryInfo",
];

const orderFromData = (data) => {
  const ord = Array.isArray(data?.sectionOrder) && data.sectionOrder.length
    ? data.sectionOrder
    : DEFAULT_ORDER;
  // Only keep known sections, in requested order
  const known = new Set(DEFAULT_ORDER);
  return ord.filter((s) => known.has(s));
};

/* ---------------------- Military Display Maps ---------------------- */

const mapStatus = (v) =>
  displayValues?.militaryStatus?.[v]
    ? stripHtml(displayValues.militaryStatus[v])
    : (v || "");

const mapBranch = (v) =>
  displayValues?.militaryBranches?.[v]
    ? stripHtml(displayValues.militaryBranches[v])
    : (v || "");

const mapRank = (branch, rank) => {
  try {
    const m = displayValues?.militaryRanks?.[branch]?.[rank];
    return m ? stripHtml(m) : (rank || "");
  } catch {
    return rank || "";
  }
};

/* --------------------------- Main Builder -------------------------- */

export function buildResumeDoc(data) {
  const federal = isFederalResume(data);
  const personal = data?.personalInfo ?? {};
  const firstName = stripHtml(personal.firstName || "");
  const lastName = stripHtml(personal.lastName || "");
  const phone = stripHtml(personal.phoneNumber || "");
  const email = stripHtml(personal.email || "");
  const socialPresence = stripHtml(personal.socialPresence || "");
  const street = stripHtml(personal.street || "");
  const city = stripHtml(personal.city || "");
  const state = stripHtml(personal.state || "");
  const postalCode = stripHtml(personal.postalCode || "");
  const country = stripHtml(personal.country || "");

  // Hiring Authorities display names (if any)
  const authorities = Array.isArray(personal.authority) ? personal.authority : [];
  const authorityDisplayNames = authorities.map((code) => {
    const match = hiringAuthorities.find((entry) => entry.value === code);
    return stripHtml(match ? match.displayValue : code);
  });

  const paragraphs = [];

  // Header: Name
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    // Render the candidate's name using our helper with a larger size.
    paragraphs.push(makeTextParagraph(fullName, { bold: true, size: 32 }));
  }

  // Contact lines
  const contactLine = [email, phone, socialPresence].filter(Boolean).join(" | ");
  if (contactLine) paragraphs.push(makeTextParagraph(contactLine));
  // const address1 = [street].filter(Boolean).join(", ");
  const address2 = (federal
    ? [street, city, state, postalCode]
    : [city, state]
  ).filter(Boolean).join(" | ");
  const address3 = country;
  // if (address1) paragraphs.push(makeTextParagraph(address1));
  if (address2) paragraphs.push(makeTextParagraph(address2));
  if (address3) paragraphs.push(makeTextParagraph(address3));
  if (federal && authorityDisplayNames.length > 0) {
    paragraphs.push(makeTextParagraph(`Hiring Authorities: ${authorityDisplayNames.join(" | ")}`, { bold: true }));
  }
  // Ensure a blank line separates the personal info block from the first resume section.
  // Without this, the first heading may immediately follow the address line.
  paragraphs.push(makeTextParagraph(" "));

  // Section rendering by configured order
  const ordered = orderFromData(data);

  for (const sec of ordered) {
    switch (sec) {
      case "professionalSummary": {
        const summary = stripHtml(data?.professionalSummary || "");
        const showSummary = data?.showProfessionalSummary !== false; // default true
        if (summary && showSummary) {
          // Use bold text without docx heading styles for section titles.
          paragraphs.push(makeHeading("Professional Summary"));
          paragraphs.push(makeTextParagraph(summary));
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

      case "skills": {
        const selected = Array.isArray(data?.selectedSkills) && data.selectedSkills.length
          ? data.selectedSkills
          : (Array.isArray(data?.skills) ? data.skills : []);
        const inline = pipeInline(selected);
        if (inline) {
          paragraphs.push(makeHeading("Skills"));
          paragraphs.push(inline);
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

 case "workHistory": {
  const work = Array.isArray(data?.workHistory)
    ? data.workHistory.filter(isWorkEntryVisible)
    : [];
  if (work.length > 0) {
    paragraphs.push(makeHeading("Work Experience"));

    work.forEach((job) => {
      if (!job) return;

      const jobTitle = stripHtml(job.jobTitle || "");
      const employer = stripHtml(job.employer || "");
      const employerAddress = stripHtml(job.employerAddress || "");
      const startDate = stripHtml(job.startDate || "");
      const endDate = stripHtml(job.endDate || "");
      const present = !!job.present;
      const hoursPerWeek = stripHtml(job.hoursPerWeek || "");
      const payAmount = stripHtml(job.payAmount || "");
      const payRateValue = stripHtml(job.payRate || "");
      const employmentTypeValue = stripHtml(job.employmentType || "");
      const payRate = stripHtml(displayValues?.payRate?.[payRateValue] || payRateValue);
      const employmentType = stripHtml(displayValues?.employmentType?.[employmentTypeValue] || employmentTypeValue);
      const supervisor = stripHtml(job.supervisor || "");
      const supervisorContact = stripHtml(job.supervisorContact || "");
      const canContactSupervisor = stripHtml(job.canContactSupervisor || "");

      // Only map the supervisor contact preference.
      let contactDisplay = canContactSupervisor;
      try {
        if (displayValues?.canContactSupervisor?.[canContactSupervisor]) {
          contactDisplay = stripHtml(displayValues.canContactSupervisor[canContactSupervisor]);
        }
      } catch {}

      // Build a single inline line with employer, address, and dates
      const parts = [];
      if (jobTitle || employer) {
        parts.push(`${jobTitle}${employer ? ` — ${employer}` : ""}`);
      }
      if (federal && employerAddress) parts.push(employerAddress);
      const dateRange = formatResumeDateRange(startDate, endDate, present);
      if (dateRange) parts.push(dateRange);

      if (parts.length) {
        paragraphs.push(makeTextParagraph(parts.join(" | ")));
      }

      // Hours per week (pay rate and employment type omitted)
      if (federal && hoursPerWeek) {
        paragraphs.push(makeTextParagraph(`Hours/Week: ${hoursPerWeek}`));
      }

      if (federal && (employmentType || payAmount)) {
        const federalDetails = [
          employmentType ? `Employment Type: ${employmentType}` : "",
          payAmount ? `Pay: ${payAmount}${payRate ? ` ${payRate}` : ""}` : "",
        ].filter(Boolean);
        paragraphs.push(makeTextParagraph(federalDetails.join(" | ")));
      }

      if (federal && supervisor) {
        let supervisorLine = `Supervisor: ${supervisor}${supervisorContact ? ` (${supervisorContact})` : ""}`;
        if (contactDisplay) {
          supervisorLine += ` (${contactDisplay})`;
        }
        paragraphs.push(makeTextParagraph(supervisorLine));
      }

      // Bullets — enhanced first, then bullets, then legacy
      const bullets = collectWorkBullets(job);
      if (bullets.length) {
        paragraphs.push(...makeBulletList(bullets));
      }

      paragraphs.push(makeTextParagraph(" "));
    });
  }
  break;
}


case "educationHistory": {
  const eduList = Array.isArray(data?.educationHistory)
    ? data.educationHistory.filter(isEducationEntryVisible)
    : [];
  if (eduList.length > 0) {
    paragraphs.push(makeHeading("Education"));

    eduList.forEach((edu) => {
      if (!edu) return;

      const degree = stripHtml(edu.degree || "");
      const institution = stripHtml(edu.institution || "");
      const major = stripHtml(edu.major || "");
      const minor = stripHtml(edu.minor || "");
      const showMinor = edu.showMinor !== false;
      const gpa = stripHtml(edu.gpa || "");
      const showGPA = edu.showGPA !== false;
      const graduationYear = stripHtml(edu.graduationYear || "");
      const startDate = stripHtml(edu.startDate || "");
      const endDate = stripHtml(edu.endDate || "");
      const present = !!edu.present;

      // Build title line: degree | institution | Major: X | Minor: Y (if shown) | GPA: X | Graduation Year: XXXX
      const titleParts = [
        degree,
        institution,
        major ? `Major: ${major}` : null,
        showMinor && minor ? `Minor: ${minor}` : null,
        showGPA && gpa ? `GPA: ${gpa}` : null,
        graduationYear ? `Year: ${graduationYear}` : null
      ].filter(Boolean);

      if (titleParts.length) {
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: titleParts.join(" | "),
                bold: true,
                font: DEFAULT_FONT,
                size: NORMAL_SIZE,
              }),
            ],
          })
        );
      }


      // Dates (same as before)
      const dateText = formatResumeDateRange(startDate, endDate, present)
        || (graduationYear ? `Graduation Year: ${graduationYear}` : "");
      if (dateText) paragraphs.push(makeTextParagraph(dateText));

      if (showGPA && gpa) paragraphs.push(makeTextParagraph(`GPA: ${gpa}`));

      // Relevant Courses (pipe-separated)
      const relevantCourses =
        Array.isArray(edu.relevantCoursesBullets) && edu.relevantCoursesBullets.length
          ? edu.relevantCoursesBullets
          : (Array.isArray(edu.relevantCourses) ? edu.relevantCourses : []);
      const coursesInline = pipeInline(relevantCourses);
      if (coursesInline) {
        paragraphs.push(makeTextParagraph("Relevant Coursework:", { italics: true }));
        paragraphs.push(coursesInline);
      }

      // Projects
      // Prefer any explicitly provided enhancedProjects array on the education entry.
      // If that is absent or empty, fall back to the significantProjects array. Each
      // significantProjects entry may be either a plain string/number or an object
      // with text/enhancedProject fields. Without this fallback, projects entered
      // in the form (stored under significantProjects) would not show up in the
      // generated document. See issue report for details.
      let eduProjects = [];
      if (Array.isArray(edu.enhancedProjects) && edu.enhancedProjects.length) {
        eduProjects = edu.enhancedProjects;
      } else if (Array.isArray(edu.significantProjects) && edu.significantProjects.length) {
        eduProjects = edu.significantProjects
          .map((p) => {
            if (p == null) return "";
            if (typeof p === "string" || typeof p === "number") {
              return stripHtml(String(p));
            }
            // p is an object; prefer enhancedProject, then text
            const txt = p.enhancedProject || p.text || p;
            return stripHtml(typeof txt === "string" || typeof txt === "number" ? txt : String(txt));
          })
          .filter((s) => s && s.trim().length > 0);
      }
      if (eduProjects.length) {
        paragraphs.push(makeTextParagraph("Projects:", { italics: true }));
        paragraphs.push(...makeBulletList(eduProjects));
      }

      paragraphs.push(makeTextParagraph(" "));
    });
  }
  break;
}



      case "certifications": {
        const certs = Array.isArray(data?.certifications) ? data.certifications : [];
        if (certs.length > 0) {
          paragraphs.push(makeHeading("Certifications"));
          // Build nice label per cert, then pipe them
          const items = certs
            .map((c) => {
              if (!c) return "";
              const name = stripHtml(c.certificationName || "");
              const auth = stripHtml(c.certificationAuthority || "");
              const issue = stripHtml(c.issueDate || "");
              const expiry = stripHtml(c.expiryDate || "");
              const showExpiry = c.showExpiryDate !== false; // default true
              let label = name;
              if (auth) label += (label ? " — " : "") + auth;
              let dates = "";
              if (issue) dates = issue;
              if (showExpiry && expiry) dates = dates ? `${dates} to ${expiry}` : expiry;
              if (dates) label += (label ? ` (${dates})` : dates);
              return label;
            })
            .filter((s) => s && s.trim().length > 0);
          const inline = pipeInline(items);
          if (inline) paragraphs.push(inline);
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

      case "training": {
        const trainings = Array.isArray(data?.training) ? data.training : [];
        if (trainings.length > 0) {
          paragraphs.push(makeHeading("Training"));
          const inline = pipeInline(trainings);
          if (inline) paragraphs.push(inline);
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

      case "volunteerWork": {
        const vols = Array.isArray(data?.volunteerWork) ? data.volunteerWork : [];
        if (vols.length > 0) {
          paragraphs.push(makeHeading("Volunteer Experience"));
          // Convert each entry into a descriptive string before creating bullets
          const volunteerBullets = vols
            .map((v) => formatVolunteer(v, federal))
            .filter((t) => t && t.trim().length > 0);
          if (volunteerBullets.length > 0) {
            paragraphs.push(...makeBulletList(volunteerBullets));
          }
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

      case "languages": {
        const langs = Array.isArray(data?.languages) ? data.languages : [];
        const inline = pipeInline(langs);
        if (inline) {
          paragraphs.push(makeHeading("Languages"));
          paragraphs.push(inline);
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

      case "interests": {
        const ints = Array.isArray(data?.interests) ? data.interests : [];
        const inline = pipeInline(ints);
        if (inline) {
          paragraphs.push(makeHeading("Interests & Hobbies"));
          paragraphs.push(inline);
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

      case "awards": {
        const awds = Array.isArray(data?.awards) ? data.awards : [];
        const inline = pipeInline(awds);
        if (inline) {
          paragraphs.push(makeHeading("Awards & Honors"));
          paragraphs.push(inline);
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

      case "organizations": {
        const orgs = Array.isArray(data?.organizations) ? data.organizations : [];
        const inline = pipeInline(orgs);
        if (inline) {
          paragraphs.push(makeHeading("Organizations"));
          paragraphs.push(inline);
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

      case "references": {
        const refs = Array.isArray(data?.references) ? data.references : [];
        if (refs.length > 0) {
          paragraphs.push(makeHeading("References"));
          refs.forEach((r) => {
            if (!r) return;
            const name = stripHtml(r.name || "");
            const relationship = stripHtml(r.relationship || "");
            const emailR = stripHtml(r.email || "");
            const phoneR = stripHtml(r.phone || r.phoneNumber || "");
            const notes = stripHtml(r.notes || "");
            let headerLine = name;
            if (relationship) headerLine += headerLine ? ` (${relationship})` : relationship;
            if (headerLine) paragraphs.push(makeTextParagraph(headerLine));
            const contact = [phoneR, emailR].filter(Boolean).join(" | ");
            if (contact) paragraphs.push(makeTextParagraph(contact));
            if (notes) paragraphs.push(makeTextParagraph(notes));
            paragraphs.push(makeTextParagraph(" "));
          });
        }
        break;
      }

      case "additionalInformation": {
        const text = stripHtml(data?.additionalInformation || "");
        if (text) {
          paragraphs.push(makeHeading("Additional Information"));
          paragraphs.push(makeTextParagraph(text));
          paragraphs.push(makeTextParagraph(" "));
        }
        break;
      }

case "militaryInfo": {
  const mil = data?.militaryInfo || {};
  const status = mapStatus(mil.militaryStatus || "");

  // If no status is selected, skip everything
  if (!status || !status.trim()) {
    break;
  }

  const branch = mapBranch(mil.militaryBranch || "");
  const rank = mapRank(mil.militaryBranch, mil.militaryRank || "");
  const occupation = stripHtml(mil.militaryOccupation || "");
  const location = stripHtml(mil.militaryLocation || "");
  const duration = stripHtml(mil.militaryDuration || "");

  const line1 = [status, branch, rank, duration, occupation, location].filter((s) => s && s.trim()).join(" | ");

  if (line1) {
    paragraphs.push(makeHeading("Military Service"));
    if (line1) paragraphs.push(makeTextParagraph(line1));
    paragraphs.push(makeTextParagraph(" "));
  }
  break;
}


      case "personalInfo": {
        // Already rendered in header
        break;
      }

      default:
        break;
    }
  }

  // Hiring Authorities (if any) — at end
return new Document({
  creator: "Resume Builder",
  title: "Resume",
  sections: [
    {
      properties: {
        page: {
          margin: {
            top: 360,     // 0.5 inch
            right: 360,   // 0.5 inch
            bottom: 360,  // 0.5 inch
            left: 360,    // 0.5 inch
          },
        },
      },
      children: paragraphs,
    },
  ],
});

}

export default buildResumeDoc;
